import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, Radio, RadioGroup, Select, Stack,
  Step, StepDescription, StepIcon, StepIndicator, StepNumber, StepSeparator, StepStatus, StepTitle, Stepper, useSteps, FormControl, FormLabel, ButtonGroup, VStack, Textarea
} from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import { useAppContext } from 'hooks/useAppContext'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import {
  getCommunicationOptionById, getCommunicationOptions,
  getDisplayPricingById, getDisplayPricingByIds,
  getExistingSignOptionById, getExistingSignOptions,
  getInstallationOptionById, getInstallationOptions,
  getShippingOptionById, getShippingOptions,
  getPedestalDecorationOptionById, getPedestalDecorationOptions,
  getSiteSurveyOptionById, getSiteSurveyOptions,
  getVerticalSupportOptionById, getVerticalSupportOptions,
  getFooterStyleOptionById, getFooterStyleOptions, fetchDataSafely,
  convertEdgeQuoteToProjectById,
  getDisplayPricingNewByQuoteId
} from 'api'
import { DISPLAY_PRICING_PITCHES } from 'components/system/display-pricings/DisplayPricingForm'
import { CABINET_FACES, CABINET_FACE_TYPES, CABINET_STYLES, EMC_BOARD_MATRIX_MULTIPLIERS, EMC_FACES, EMC_MODELS, EMC_PITCHES, MONUMENT_STYLES, PEDESTAL_STYLES } from 'lib'
import { AccountingInfoStep, defaultAccountingInfo } from 'components/quotes/AccountingInfoStep'

const steps = [
  { title: 'First', description: 'Client Info' },
  { title: 'Second', description: 'Quote Options' },
  { title: 'Third', description: 'Accounting Info' },
  { title: 'Fourth', description: 'Project Overview' }
]

const POLE_INSTALLATION_TYPES = ['direct_burial', 'single_anchor', 'dual_anchor']

const isNotFoundError = (err) => {
  const message = typeof err === 'string' ? err : err?.error
  return typeof message === 'string' && /not found/i.test(message)
}

export const ModalConvertToProject = ({ quote, customer, isOpen, onClose }) => {
  const { createToast } = useAppContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emcOriginalPricing, setEmcOriginalPricing] = useState(null)
  const [emcAlternateOptions, setEmcAlternateOptions] = useState([])
  const [form, setForm] = useImmer({
    clientInfo: {
      company: '',
      contactName: '',
      email: '',
      phone: '',
      address: '',
      state: '',
      city: '',
      zip: ''
    },
    quoteOptions: {
      noEmc: true,
      emcAlternateOptionSelect: 'No',
      emcPricingVersion: 0,
      emcModel: '',
      emcFace: '',
      emcNominalSize: '',
      emcPitch: '',
      emcMatrix: '',
      emcCommunicationOption: null,
      noCabinet: true,
      cabinetStyle: '',
      cabinetFace: '',
      cabinetFaceType: '',
      cabinetNominalSize: '',
      cabinetCphra: false,
      cabinetPlpr: false,
      cabinetCustomerArtConfirmation: '',
      noPedestal: true,
      pedestalStyle: '',
      pedestalNominalSize: '',
      pedestalDecorationOption: null,
      pedestalAdditionalHeight: 0,
      noMonument: true,
      monumentStyle: '',
      monumentDescription: '',
      numberOfSupports: 0,
      verticalSupportOption: null,
      installationOption: null,
      siteSurveyOption: null,
      existingSignOption: null,
      additionalItems: [],
      customColorChecked: false
    },
    scope: '',
    signedQuote: null,
    signedArt: null,
    taxExemption: null,
    accountingInfo: defaultAccountingInfo()
  })

  const { activeStep, setActiveStep } = useSteps({
    index: 1,
    count: steps.length
  })

  useEffect(() => {
    if (!quote?.emcAlternateOptions) return
    const load = async () => {
      try {
        if (quote?.emcPricingVersion === 2) {
          const resp = await getDisplayPricingNewByQuoteId(quote?.id)
          setEmcAlternateOptions(JSON.parse(quote?.emcAlternateOptions).map(v => ({ id: v, pitch: v, dimsH: resp.modsH, dimsW: resp.modsL, calculatedBoardMatrix: `${(resp.modsH || 0) * (EMC_BOARD_MATRIX_MULTIPLIERS[v || ''] || 0)} x ${(resp.modsL || 0) * (EMC_BOARD_MATRIX_MULTIPLIERS[v || ''] || 0)}` })))
        } else {
          const resp = await getDisplayPricingByIds(JSON.parse(quote?.emcAlternateOptions).map(Number) || [])
          setEmcAlternateOptions(resp)
        }
      } catch (err) {
        createToast('error', 'Error', err)
      }
    }
    load()
  }, [quote?.emcAlternateOptions])

  useEffect(() => {
    if (!customer?.id) return
    setForm(draft => {
      draft.clientInfo = {
        company: customer.company || '',
        contactName: `${customer.firstname || ''} ${customer.lastname || ''})`,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        state: customer.state || '',
        city: customer.city || '',
        zip: customer.zip || ''
      }
    })
  }, [customer?.id])

  useEffect(() => {
    if (!quote?.id || !isOpen) return

    setActiveStep(1)

    const load = async () => {
      const quoteOptions = {}
      quoteOptions.noEmc = quote.noEmc === 1
      quoteOptions.noCabinet = quote.noCabinet === 1
      quoteOptions.noPedestal = quote.noPedestal === 1
      quoteOptions.noMonument = quote.noMonument === 1
      quoteOptions.emcPricingVersion = quote.emcPricingVersion
      if (quoteOptions.noEmc === false) {
        let emcPricing = null
        if (quote.emcPricingVersion === 2) {
          const resp = await getDisplayPricingNewByQuoteId(quote?.id)
          emcPricing = { id: resp.pitch, pitch: resp.pitch, dimsH: resp.modsH, dimsW: resp.modsL, calculatedBoardMatrix: `${(resp.modsH || 0) * (EMC_BOARD_MATRIX_MULTIPLIERS[resp.pitch || ''] || 0)} x ${(resp.modsL || 0) * (EMC_BOARD_MATRIX_MULTIPLIERS[resp.pitch || ''] || 0)}` }
        } else {
          emcPricing = await fetchDataSafely(() => getDisplayPricingById(quote.emcPricingId), err => createToast('error', 'Error', err))
        }
        const emcCommunicationOption = quote.emcCommunicationOptionId
          ? await fetchDataSafely(() => getCommunicationOptionById(quote.emcCommunicationOptionId), err => createToast('error', 'Error', err))
          : null
        setEmcOriginalPricing(emcPricing)
        quoteOptions.emcAlternateOptionSelect = 'No'
        quoteOptions.emcModel = quote.emcModel || EMC_MODELS.G4
        quoteOptions.emcFace = quote.emcFace
        quoteOptions.emcNominalSize = `${emcPricing.dimsH} x ${emcPricing.dimsW}`
        quoteOptions.emcPitch = emcPricing.pitch
        quoteOptions.emcMatrix = emcPricing.calculatedBoardMatrix
        quoteOptions.emcCommunicationOption = emcCommunicationOption
      } else {
        quoteOptions.emcAlternateOptionSelect = 'No'
        quoteOptions.emcModel = ''
        quoteOptions.emcFace = ''
        quoteOptions.emcNominalSize = ''
        quoteOptions.emcPitch = ''
        quoteOptions.emcMatrix = ''
        quoteOptions.emcCommunicationOption = null
      }

      if (quoteOptions.noCabinet === false) {
        quoteOptions.cabinetStyle = quote.cabinetStyle
        quoteOptions.cabinetFace = quote.cabinetFace
        quoteOptions.cabinetFaceType = quote.cabinetFaceType || 'Polycarbonate'
        quoteOptions.cabinetNominalSize = `${quote.cabinetHeightFt}'-${quote.cabinetHeightIn}" x ${quote.cabinetLengthFt}'-${quote.cabinetLengthIn}"`
        quoteOptions.cabinetCphra = quote.cphra === 1
        quoteOptions.cabinetPlpr = quote.plpr === 1
        quoteOptions.cabinetCustomerArtConfirmation = ''
      } else {
        quoteOptions.cabinetStyle = ''
        quoteOptions.cabinetFace = ''
        quoteOptions.cabinetFaceType = ''
        quoteOptions.cabinetNominalSize = ''
        quoteOptions.cabinetCphra = false
        quoteOptions.cabinetPlpr = false
        quoteOptions.cabinetCustomerArtConfirmation = ''
      }

      if (quoteOptions.noPedestal === false) {
        const pedestalDecorationOption = quote.pedestalDecorationOptionId
          ? await fetchDataSafely(() => getPedestalDecorationOptionById(quote.pedestalDecorationOptionId), err => createToast('error', 'Error', err))
          : null
        quoteOptions.pedestalStyle = quote.pedestalStyle
        quoteOptions.pedestalNominalSize = `${parseInt(quote.pedestalHeightIn / 12)}'-${quote.pedestalHeightIn % 12}" x ${parseInt(quote.pedestalLengthIn / 12)}'-${quote.cabinetLengthIn % 12}"`
        quoteOptions.pedestalDecorationOption = pedestalDecorationOption
        quoteOptions.pedestalAdditionalHeight = 0
      } else {
        quoteOptions.pedestalStyle = ''
        quoteOptions.pedestalNominalSize = ''
        quoteOptions.pedestalDecorationOption = null
        quoteOptions.pedestalAdditionalHeight = quote.pedestalAdditionalHeight
      }

      if (quoteOptions.noMonument === false) {
        quoteOptions.monumentStyle = quote.monumentStyle
        quoteOptions.monumentDescription = quote.monumentDescription
      } else {
        quoteOptions.monumentStyle = ''
        quoteOptions.monumentDescription = ''
      }

      const fetchOptionIfSet = (id, fetchFn) => {
        const numericId = Number(id)
        if (!Number.isFinite(numericId) || numericId <= 0) {
          return Promise.resolve(null)
        }
        return fetchDataSafely(() => fetchFn(numericId), (err) => {
          if (!isNotFoundError(err)) {
            createToast('error', 'Error', err)
          }
        })
      }

      const [verticalSupportOption, footerStyleOption, installationOption, siteSurveyOption, shippingOption, existingSignOption] = await Promise.all([
        fetchOptionIfSet(quote.verticalSupportOptionId, getVerticalSupportOptionById),
        fetchOptionIfSet(quote.footerStyleOptionId, getFooterStyleOptionById),
        fetchOptionIfSet(quote.installationOptionId, getInstallationOptionById),
        fetchOptionIfSet(quote.siteSurveyOptionId, getSiteSurveyOptionById),
        fetchOptionIfSet(quote.shippingOptionId, getShippingOptionById),
        fetchOptionIfSet(quote.existingSignOptionId, getExistingSignOptionById)
      ])
      quoteOptions.numberOfSupports = quote.numberOfSupports
      quoteOptions.installationType = quote.installationType || ''
      quoteOptions.autoTubeSize = quote.autoTubeSize || ''

      let resolvedVerticalSupportOption = verticalSupportOption
      if (!resolvedVerticalSupportOption && quote.installationType && (POLE_INSTALLATION_TYPES.includes(quote.installationType) || quote.autoTubeSize)) {
        const tubeDetail = (quote.isSupportOverride && quote.overrideTubeSize)
          ? quote.overrideTubeSize
          : (quote.autoTubeSize || '')
        if (tubeDetail && tubeDetail !== '—') {
          resolvedVerticalSupportOption = {
            id: quote.verticalSupportOptionId || 0,
            description: tubeDetail,
            baseCost: 0,
            footCost: 0
          }
        }
      }
      quoteOptions.verticalSupportOption = resolvedVerticalSupportOption
      quoteOptions.footerStyleOption = footerStyleOption
      quoteOptions.installationOption = installationOption
      quoteOptions.siteSurveyOption = siteSurveyOption
      quoteOptions.shippingOption = shippingOption
      quoteOptions.existingSignOption = existingSignOption
      quoteOptions.mowCurbEnabled = quote.mowCurbEnabled || false
      quoteOptions.mowCurbCalculationType = quote.mowCurbCalculationType || ''
      quoteOptions.mowCurbWidthFt = quote.mowCurbWidthFt || 0
      quoteOptions.customColorChecked = quote.customColorChecked
      quoteOptions.additionalItems = quote.additionalItems ? JSON.parse(quote.additionalItems) : []

      setForm(draft => {
        draft.quoteOptions = quoteOptions
        draft.scope = ''
        draft.signedQuote = null
        draft.signedArt = null
        draft.taxExemption = null
        draft.accountingInfo = defaultAccountingInfo(quote.paymentTerms || '')
      })
    }
    load()
  }, [quote?.id, isOpen])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const project = await convertEdgeQuoteToProjectById(quote?.id, { ...form })
      createToast('success', 'Project created.')
      setIsSubmitting(false)
      window.open(`/projects/detail/${project.id}`)
      onClose()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="xl">Convert To Project. Quote #{quote?.quoteNo}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stepper index={activeStep} mb={5}>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>

                <Box flexShrink='0'>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </Box>

                <StepSeparator />
              </Step>
            ))}
          </Stepper>
          {activeStep === 1 && (
            <FirstStep
              form={form}
              setForm={setForm}
              onNext={() => setActiveStep(2)}
            />
          )}
          {activeStep === 2 && (
            <SecondStep
              form={form}
              setForm={setForm}
              onPrev={() => setActiveStep(1)}
              onNext={() => setActiveStep(3)}
              emcOriginalPricing={emcOriginalPricing}
              emcAlternateOptions={emcAlternateOptions}
            />
          )}
          {activeStep === 3 && (
            <AccountingInfoStep
              form={form}
              setForm={setForm}
              onPrev={() => setActiveStep(2)}
              onNext={() => setActiveStep(4)}
            />
          )}
          {activeStep === 4 && (
            <ThirdStep
              form={form}
              setForm={setForm}
              onPrev={() => setActiveStep(3)}
              onNext={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

ModalConvertToProject.propTypes = {
  quote: PropTypes.object,
  customer: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}

const FirstStep = ({ form, setForm, onNext, ...rest }) => {
  const { clientInfo } = form

  const handleChange = (e) => {
    setForm(draft => {
      draft.clientInfo = {
        ...draft.clientInfo,
        [e.target.name]: e.target.value
      }
    })
  }
  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit}>
        <Grid
          templateColumns="1fr 1fr"
          columnGap={5}
          rowGap={2}
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Company Name</FormLabel>
              <Input name="company" value={clientInfo.company} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Phone Number</FormLabel>
              <Input name="phone" value={clientInfo.phone} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Contact Name</FormLabel>
              <Input name="contactName" value={clientInfo.contactName} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input name="email" value={clientInfo.email} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Address</FormLabel>
              <Input name="address" value={clientInfo.address} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>State</FormLabel>
              <Input name="state" value={clientInfo.state} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>City</FormLabel>
              <Input name="city" value={clientInfo.city} onChange={handleChange} />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Zip</FormLabel>
              <Input name="zip" value={clientInfo.zip} onChange={handleChange} />
            </FormControl>
          </GridItem>
        </Grid>
        <ButtonGroup w="100%" justifyContent="flex-end" mt={5}>
          <Button type="button" size="sm" colorScheme="blue" isDisabled={true}>Previous</Button>
          <Button type="submit" size="sm" colorScheme="blue">Next</Button>
        </ButtonGroup>
      </form>
    </Box>
  )
}

FirstStep.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  onNext: PropTypes.func
}

const SecondStep = ({ form, setForm, onPrev, onNext, emcOriginalPricing, emcAlternateOptions, ...rest }) => {
  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]

    if (file) {
      setForm(draft => {
        draft[e.target.name] = e.target.files[0]
      })
    }
  }

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit}>
        <VStack gap={5}>
          {form.quoteOptions.noEmc === false && (
            <EmcQuoteOptions
              form={form}
              setForm={setForm}
              emcOriginalPricing={emcOriginalPricing}
              emcAlternateOptions={emcAlternateOptions}
            />
          )}
          {form.quoteOptions.noCabinet === false && (
            <CabinetQuoteOptions
              form={form}
              setForm={setForm}
            />
          )}
          <PedestalQuoteOptions
            form={form}
            setForm={setForm}
          />
          {/* {form.quoteOptions.noMonument === false && (
            <MonumentQuoteOptions
              form={form}
              setForm={setForm}
            />
          )} */}
          <SupportQuoteOptions
            form={form}
            setForm={setForm}
          />
          <ExtraQuoteOptions
            form={form}
            setForm={setForm}
          />
          <Box w="100%">
            <Grid
              templateColumns="1fr 1fr"
              columnGap={5}
            >
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Signed Quote</FormLabel>
                  <Input type="file" name="signedQuote" accept=".pdf" onChange={handleFileChange} />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Signed Art</FormLabel>
                  <Input type="file" name="signedArt" accept=".pdf" onChange={handleFileChange} />
                </FormControl>
              </GridItem>
            </Grid>
          </Box>
          <AdditionalItems
            form={form}
            setForm={setForm}
          />
        </VStack>
        <ButtonGroup w="100%" justifyContent="flex-end" mt={5}>
          <Button type="button" size="sm" colorScheme="blue" onClick={onPrev}>Previous</Button>
          <Button type="submit" size="sm" colorScheme="blue">Next</Button>
        </ButtonGroup>
      </form>
    </Box>
  )
}

SecondStep.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  emcOriginalPricing: PropTypes.object,
  emcAlternateOptions: PropTypes.arrayOf(PropTypes.object)
}

const EmcQuoteOptions = ({ form, setForm, emcOriginalPricing, emcAlternateOptions, ...rest }) => {
  const [isLocked, setIsLocked] = useState(true)
  const { quoteOptions } = form
  const loadCommunicationOptions = useCallback((page, search) => getCommunicationOptions(page, 20, search), [])

  const handleChange = (e) => {
    setForm(draft => {
      draft.quoteOptions[e.target.name] = e.target.value
    })
  }

  const handleAlternateOptionChange = (e) => {
    let selectedOption = null
    if (e.target.value === 'No') {
      selectedOption = emcOriginalPricing
    } else {
      let optionId = 0
      if (form.quoteOptions.emcPricingVersion === 2) {
        optionId = e.target.value
      } else {
        optionId = parseInt(e.target.value)
      }
      selectedOption = emcAlternateOptions.filter(v => v.id === optionId)?.[0]
    }
    if (!selectedOption) return
    setForm(draft => {
      draft.quoteOptions.emcAlternateOptionSelect = selectedOption.id
      draft.quoteOptions.emcNominalSize = `${selectedOption.dimsH} x ${selectedOption.dimsW}`
      draft.quoteOptions.emcPitch = selectedOption.pitch
      draft.quoteOptions.emcMatrix = selectedOption.calculatedBoardMatrix
    })
  }

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>EMC</Heading>
      <Grid
        templateColumns="1fr 1fr"
        columnGap={5}
        rowGap={2}
      >
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Alternate Options</FormLabel>
            <Select name="emcAlternateOptionSelect" value={quoteOptions.emcAlternateOptionSelect} onChange={handleAlternateOptionChange} isDisabled={isLocked}>
              <option value="No">No</option>
              {emcAlternateOptions.map(option => <option key={option.id} value={option.id}>{option.pitch}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem></GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Model</FormLabel>
            <Input name="emcModel" value={quoteOptions.emcModel} onChange={handleChange} isReadOnly={isLocked} />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Configuration</FormLabel>
            <Select name="emcFace" value={quoteOptions.emcFace} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
              {Object.values(EMC_FACES).map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Nominal Size</FormLabel>
            <Input name="emcNominalSize" value={quoteOptions.emcNominalSize} onChange={handleChange} isReadOnly={isLocked} />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Pitch</FormLabel>
            <Select name="emcPitch" value={quoteOptions.emcPitch} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
              {Object.values(form.quoteOptions.emcPricingVersion === 2 ? EMC_PITCHES : DISPLAY_PRICING_PITCHES).map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Matrix</FormLabel>
            <Input name="emcMatrix" value={quoteOptions.emcMatrix} onChange={handleChange} isReadOnly={isLocked} />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Communication</FormLabel>
            <UiSelect
              value={quoteOptions.emcCommunicationOption}
              onChange={v => {
                setForm(draft => { draft.quoteOptions.emcCommunicationOption = v })
              }}
              loadData={loadCommunicationOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Communication Options Found'}
              isSearchable={false}
              required={true}
              isDisabled={isLocked}
            />
          </FormControl>
        </GridItem>
        <GridItem></GridItem>
        <GridItem justifySelf="flex-end">
          <Button
            type="button"
            size="md"
            variant="ghost"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? 'UNLOCK INPUT' : 'LOCK INPUT'}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  )
}

EmcQuoteOptions.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  emcOriginalPricing: PropTypes.object,
  emcAlternateOptions: PropTypes.arrayOf(PropTypes.object)
}

const CabinetQuoteOptions = ({ form, setForm, ...rest }) => {
  const [isLocked, setIsLocked] = useState(true)
  const { quoteOptions } = form

  const handleChange = (e) => {
    setForm(draft => {
      draft.quoteOptions[e.target.name] = e.target.value
    })
  }

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Cabinet</Heading>
      <Grid
        templateColumns="1fr 1fr"
        columnGap={5}
        rowGap={2}
      >
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Cabinet Style</FormLabel>
            <Select name="cabinetStyle" value={quoteOptions.cabinetStyle} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
              {Object.values(CABINET_STYLES).map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Configuration</FormLabel>
            <Select name="cabinetFace" value={quoteOptions.cabinetFace} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
              {Object.values(CABINET_FACES).map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Face Type</FormLabel>
            <Select name="cabinetFaceType" value={quoteOptions.cabinetFaceType || 'Polycarbonate'} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
              {Object.values(CABINET_FACE_TYPES).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Nominal Size</FormLabel>
            <Input name="cabinetNominalSize" value={quoteOptions.cabinetNominalSize} onChange={handleChange} isReadOnly={isLocked} />
          </FormControl>
        </GridItem>
        <GridItem alignSelf="flex-end">
          <FormControl isRequired>
            <RadioGroup
              onChange={(v) => {
                if (v === 'cphra') {
                  setForm(draft => {
                    draft.quoteOptions.cabinetCphra = true
                    draft.quoteOptions.cabinetPlpr = false
                  })
                }
                if (v === 'plpr') {
                  setForm(draft => {
                    draft.quoteOptions.cabinetCphra = false
                    draft.quoteOptions.cabinetPlpr = true
                  })
                }
              }}
              value={(form.quoteOptions.cabinetCphra === true && form.quoteOptions.cabinetPlpr === false) ? 'cphra' : ((form.quoteOptions.cabinetCphra === false && form.quoteOptions.cabinetPlpr === true) ? 'plpr' : '')}
            >
              <Stack direction='column'>
                <Radio size="sm" value='cphra'>Customer Vector Art</Radio>
                <Radio size="sm" value='plpr'>Professional Logo Pkg</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
        </GridItem>
        <GridItem colSpan={2}>
          <FormControl isRequired>
            <FormLabel>Has the customer signed their art confirmation?</FormLabel>
            <Select name="cabinetCustomerArtConfirmation" value={quoteOptions.cabinetCustomerArtConfirmation} onChange={handleChange} placeholder="Select">
              <option value="Yes">Yes</option>
              <option value="No">No</option>
              <option value="Not applicable">Not applicable</option>
            </Select>
          </FormControl>
        </GridItem>
        <GridItem colSpan={2} justifySelf="flex-end">
          <Button
            type="button"
            size="md"
            variant="ghost"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? 'UNLOCK INPUT' : 'LOCK INPUT'}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  )
}

CabinetQuoteOptions.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const PedestalQuoteOptions = ({ form, setForm, ...rest }) => {
  const [isLocked, setIsLocked] = useState(true)
  const { quoteOptions } = form

  const loadDecorationOptions = useCallback((page) => getPedestalDecorationOptions(page, 20), [])

  const handleChange = (e) => {
    setForm(draft => {
      draft.quoteOptions[e.target.name] = e.target.value
    })
  }

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Pedestal / Pole Cover</Heading>
      <Grid
        templateColumns="1fr 1fr"
        columnGap={5}
        rowGap={2}
      >
        {quoteOptions.noPedestal ? (
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Additional Pole Height</FormLabel>
              <Input type="number" name="pedestalAdditionalHeight" value={quoteOptions.pedestalAdditionalHeight} onChange={handleChange} isReadOnly={isLocked} />
            </FormControl>
          </GridItem>
        ) : (
          <>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>Style</FormLabel>
                <Select name="pedestalStyle" value={quoteOptions.pedestalStyle} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
                  {Object.values(PEDESTAL_STYLES).map(v => <option key={v} value={v}>{v}</option>)}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>Nominal Size</FormLabel>
                <Input name="pedestalNominalSize" value={quoteOptions.pedestalNominalSize} onChange={handleChange} isReadOnly={isLocked} />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>Decoration</FormLabel>
                <UiSelect
                  value={quoteOptions.pedestalDecorationOption}
                  onChange={v => {
                    setForm(draft => { draft.quoteOptions.pedestalDecorationOption = v })
                  }}
                  loadData={loadDecorationOptions}
                  apiRespProp="rows"
                  getOptionValue={v => v.id}
                  getOptionLabel={v => `${v.description} - ${v.cost}`}
                  placeholder="Search"
                  noOptionsMessage={() => 'No Pedestal Options Found'}
                  isSearchable={false}
                  required={true}
                  isDisabled={isLocked}
                />
              </FormControl>
            </GridItem>
          </>
        )}
        <GridItem colSpan={2} justifySelf="flex-end">
          <Button
            type="button"
            size="md"
            variant="ghost"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? 'UNLOCK INPUT' : 'LOCK INPUT'}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  )
}

PedestalQuoteOptions.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const MonumentQuoteOptions = ({ form, setForm, ...rest }) => {
  const [isLocked, setIsLocked] = useState(true)
  const { quoteOptions } = form

  const handleChange = (e) => {
    setForm(draft => {
      draft.quoteOptions[e.target.name] = e.target.value
    })
  }

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Monument</Heading>
      <Grid
        templateColumns="1fr 1fr"
        columnGap={5}
        rowGap={2}
      >
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Style</FormLabel>
            <Select name="monumentStyle" value={quoteOptions.monumentStyle} onChange={handleChange} placeholder="Select" isDisabled={isLocked}>
              {Object.values(MONUMENT_STYLES).map(v => <option key={v} value={v}>{v}</option>)}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem></GridItem>
        <GridItem colSpan={2}>
          <FormControl>
            <FormLabel>Description</FormLabel>
            <Input name="monumentDescription" value={quoteOptions.monumentDescription} onChange={handleChange} isReadOnly={isLocked} />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2} justifySelf="flex-end">
          <Button
            type="button"
            size="md"
            variant="ghost"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? 'UNLOCK INPUT' : 'LOCK INPUT'}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  )
}

MonumentQuoteOptions.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const INSTALLATION_TYPE_LABELS = {
  direct_burial: 'Direct Burial - New Footer',
  single_anchor: 'Single Pole Anchor Bolt Foundation',
  dual_anchor: 'Dual Pole Anchor Bolt Foundation',
  wall_mount: 'Wall Mount',
  retrofit: 'Retro-Fit'
}

const SupportQuoteOptions = ({ form, setForm, ...rest }) => {
  const [isLocked, setIsLocked] = useState(true)
  const { quoteOptions } = form
  const loadVerticalSupportOptions = useCallback((page) => getVerticalSupportOptions(page, 20), [])
  const loadFooterStyleOptions = useCallback((page) => getFooterStyleOptions(page, 20), [])
  const hasNewInstallationType = !!quoteOptions.installationType

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Support</Heading>
      <Grid
        templateColumns="1fr 1fr"
        columnGap={5}
        rowGap={2}
      >
        {hasNewInstallationType && (
          <GridItem>
            <FormControl>
              <FormLabel>Installation Type</FormLabel>
              <Input value={INSTALLATION_TYPE_LABELS[quoteOptions.installationType] || quoteOptions.installationType} isReadOnly />
            </FormControl>
          </GridItem>
        )}
        <GridItem>
          <FormControl isRequired>
            <FormLabel>{quoteOptions.installationType === 'wall_mount' ? 'Bracket Pairs' : '# of Supports'}</FormLabel>
            <Input
              name="numberOfSupports"
              value={quoteOptions.numberOfSupports}
              onChange={(e) => { setForm(draft => { draft.quoteOptions.numberOfSupports = e.target.value }) }}
              isReadOnly={isLocked}
            />
          </FormControl>
        </GridItem>
        {hasNewInstallationType && quoteOptions.autoTubeSize && (
          <GridItem>
            <FormControl>
              <FormLabel>Tube Size</FormLabel>
              <Input value={quoteOptions.autoTubeSize} isReadOnly />
            </FormControl>
          </GridItem>
        )}
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Vertical Support</FormLabel>
            <UiSelect
              value={quoteOptions.verticalSupportOption}
              onChange={v => {
                setForm(draft => { draft.quoteOptions.verticalSupportOption = v })
              }}
              loadData={loadVerticalSupportOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.baseCost} - $${v.footCost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Vertical Support Options Found'}
              isSearchable={false}
              required={true}
              isDisabled={isLocked}
            />
          </FormControl>
        </GridItem>
        {!hasNewInstallationType && (
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Footer Style</FormLabel>
              <UiSelect
                value={quoteOptions.footerStyleOption}
                onChange={v => {
                  setForm(draft => { draft.quoteOptions.footerStyleOption = v })
                }}
                loadData={loadFooterStyleOptions}
                apiRespProp="rows"
                getOptionValue={v => v.id}
                getOptionLabel={v => `${v.description} - $${v.baseCost} - $${v.footCost}`}
                placeholder="Search"
                noOptionsMessage={() => 'No Footer Style Options Found'}
                isSearchable={false}
                required={true}
                isDisabled={isLocked}
              />
            </FormControl>
          </GridItem>
        )}
        <GridItem></GridItem>
        <GridItem colSpan={2} justifySelf="flex-end">
          <Button
            type="button"
            size="md"
            variant="ghost"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? 'UNLOCK INPUT' : 'LOCK INPUT'}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  )
}

SupportQuoteOptions.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const ExtraQuoteOptions = ({ form, setForm, ...rest }) => {
  const [isLocked, setIsLocked] = useState(true)
  const { quoteOptions } = form
  const loadExistingSignOptions = useCallback((page) => getExistingSignOptions(page, 20), [])
  const loadInstallationOptions = useCallback((page) => getInstallationOptions(page, 20), [])
  const loadSiteSurveyOptions = useCallback((page) => getSiteSurveyOptions(page, 20), [])
  const loadShippingOptions = useCallback((page) => getShippingOptions(page, 20), [])

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Extra</Heading>
      <Grid
        templateColumns="1fr 1fr"
        columnGap={5}
        rowGap={2}
      >
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Installation</FormLabel>
            <UiSelect
              value={quoteOptions.installationOption}
              onChange={v => {
                setForm(draft => { draft.quoteOptions.installationOption = v })
              }}
              loadData={loadInstallationOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Installation Options Found'}
              isSearchable={false}
              required={true}
              isDisabled={isLocked}
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Site Survey</FormLabel>
            <UiSelect
              value={quoteOptions.siteSurveyOption}
              onChange={v => {
                setForm(draft => { draft.quoteOptions.siteSurveyOption = v })
              }}
              loadData={loadSiteSurveyOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Site Survey Options Found'}
              isSearchable={false}
              required={true}
              isDisabled={isLocked}
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Shipping</FormLabel>
            <UiSelect
              value={quoteOptions.shippingOption}
              onChange={v => {
                setForm(draft => { draft.quoteOptions.shippingOption = v })
              }}
              loadData={loadShippingOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Shipping Options Found'}
              isSearchable={false}
              required={true}
              isDisabled={isLocked}
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl isRequired>
            <FormLabel>Existing Sign</FormLabel>
            <UiSelect
              value={quoteOptions.existingSignOption}
              onChange={v => {
                setForm(draft => { draft.quoteOptions.existingSignOption = v })
              }}
              loadData={loadExistingSignOptions}
              apiRespProp="rows"
              getOptionValue={v => v.id}
              getOptionLabel={v => `${v.description} - $${v.cost}`}
              placeholder="Search"
              noOptionsMessage={() => 'No Existing Sign Options Found'}
              isSearchable={false}
              required={true}
              isDisabled={isLocked}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={2} justifySelf="flex-end">
          <Button
            type="button"
            size="md"
            variant="ghost"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? 'UNLOCK INPUT' : 'LOCK INPUT'}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  )
}

ExtraQuoteOptions.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const AdditionalItems = ({ form, setForm, ...rest }) => {
  const { quoteOptions } = form

  return (
    <Box w="100%" {...rest}>
      <Heading size="md" mb={5}>Additional Items</Heading>
      <Grid
        templateColumns="repeat(4, 1fr)"
        columnGap={5}
        rowGap={2}
      >
        {!quoteOptions.additionalItems.length && (
          <Text fontSize="sm">No items.</Text>
        )}
        {quoteOptions.additionalItems.map(item => {
          return (
            <Fragment key={item.id}>
              <GridItem colSpan={1}>
                <FormControl>
                  <FormLabel>Category</FormLabel>
                  <Input value={item.category} readOnly />
                </FormControl>
              </GridItem>
              <GridItem colSpan={3}>
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Input value={item.description} readOnly />
                </FormControl>
              </GridItem>
            </Fragment>
          )
        })}
      </Grid>
    </Box>
  )
}

AdditionalItems.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}

const ThirdStep = ({ form, setForm, onPrev, onNext, isSubmitting, ...rest }) => {
  const handleSubmit = (e) => {
    e.preventDefault()
    onNext()
  }

  return (
    <Box {...rest}>
      <form onSubmit={handleSubmit}>
        <FormControl isRequired>
          <FormLabel>Scope</FormLabel>
          <Textarea name="scope" value={form.scope} onChange={e => setForm(draft => { draft.scope = e.target.value })} />
        </FormControl>
        <ButtonGroup w="100%" justifyContent="flex-end" mt={5}>
          <Button type="button" size="sm" colorScheme="blue" onClick={onPrev}>Previous</Button>
          <Button type="submit" size="sm" colorScheme="blue" isLoading={isSubmitting}>Finish</Button>
        </ButtonGroup>
      </form>
    </Box>
  )
}

ThirdStep.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  isSubmitting: PropTypes.bool
}
